/**
 * A Break's manifest: the one definition, shared by the page and the slicing
 * script (`dsp/slicer/`). A manifest names the audio file beside it in
 * `web/breaks/` and lists the Slices, each a span of source frames and a kind.
 * `web/slicer.html` restates `KINDS` since it is not bundled; keep them in step.
 */

/** The kinds that sound, in their fixed sound-code order: kick is 1, tom is 5. */
export const KINDS = ["kick", "snare", "ghost", "hat", "tom"] as const

/** `skip` stays in the manifest but is never played, and has no sound code. */
export type Kind = (typeof KINDS)[number] | "skip"

export interface Slice {
  /** The first frame, in the source. */
  readonly start: number
  /** One past the last frame: the next Slice's start, unless edited shorter. */
  readonly end: number
  readonly kind: Kind
}

export interface Manifest {
  readonly name: string
  /** The audio file, relative to the manifest. */
  readonly file: string
  /** The file's own rate; the page decodes at it so nothing is resampled. */
  readonly sampleRate: number
  /** Only the judging page's tempo default; the core never reads it. */
  readonly bpm: number
  readonly slices: ReadonlyArray<Slice>
}

const isKind = (k: unknown): k is Kind => k === "skip" || (KINDS as ReadonlyArray<unknown>).includes(k)

const isFrame = (n: unknown): n is number => Number.isInteger(n) && (n as number) >= 0

const isSlice = (s: unknown): s is Slice => {
  if (typeof s !== "object" || s === null) return false
  const { start, end, kind } = s as Record<string, unknown>
  return isFrame(start) && isFrame(end) && end > start && isKind(kind)
}

/**
 * A hand-written guard: the page bundle carries no Effect, so no Schema. The
 * Slices must be in order and must not overlap.
 */
export const isManifest = (json: unknown): json is Manifest => {
  if (typeof json !== "object" || json === null) return false
  const m = json as Record<string, unknown>
  if (typeof m.name !== "string" || typeof m.file !== "string") return false
  if (!Number.isInteger(m.sampleRate) || (m.sampleRate as number) <= 0) return false
  if (typeof m.bpm !== "number" || !(m.bpm > 0)) return false
  if (!Array.isArray(m.slices) || !m.slices.every(isSlice)) return false
  const slices = m.slices as ReadonlyArray<Slice>
  return slices.every((s, i) => i === 0 || s.start >= slices[i - 1]!.end)
}

/** A Slice's sound code: its place in `KINDS`, counting from 1. `skip` has none. */
export const soundCode = (kind: Kind): number => (kind === "skip" ? 0 : KINDS.indexOf(kind) + 1)

/**
 * The core's Slice table: `start, length, code` triples in source frames, in
 * manifest order, with the `skip` Slices dropped. The shim copies it as is.
 */
export const sliceTable = (manifest: Manifest): Int32Array => {
  const played = manifest.slices.filter((s) => s.kind !== "skip")
  const table = new Int32Array(played.length * 3)
  played.forEach((s, i) => table.set([s.start, s.end - s.start, soundCode(s.kind)], i * 3))
  return table
}
